/**
 * Purpose: UI for adding and configuring players during game setup.
 * Owns: Player name input, age group selection, tier selection, color picker, player list.
 * Notes: Parents add each player one at a time. Each player needs a name, age group,
 *        tier (defaults to Student), and color. Max 8 players supported.
 *        Large tap targets and simple flow — one parent operating the phone for everyone.
 */

import { useState } from 'react';
import type { Player } from '../../types/game';
import type { AgeGroup, Tier } from '../../types/question';
import { createPlayer } from '../../engine/gameState';
import { MAX_PLAYERS } from '../../config/classConfig';

interface PlayerSetupProps {
  /** Current list of players already added */
  players: Player[];
  /** Called when a new player is added to the session */
  onAddPlayer: (player: Player) => void;
  /** Called when a player is removed from the session */
  onRemovePlayer: (playerId: string) => void;
}

/** Age group options with human-readable labels (CS-21: config-driven). */
const AGE_GROUP_OPTIONS: { value: AgeGroup; label: string }[] = [
  { value: 'kids-3-5', label: 'Ages 3-5' },
  { value: 'kids-6-8', label: 'Ages 6-8' },
  { value: 'kids-9-12', label: 'Ages 9-12' },
  { value: 'teens-13-17', label: 'Teens 13-17' },
  { value: 'adults-18+', label: 'Adults 18+' },
];

/** Tier options with labels (CS-21: config-driven). */
const TIER_OPTIONS: { value: Tier; label: string }[] = [
  { value: 'student', label: 'Student' },
  { value: 'scholar', label: 'Scholar' },
  { value: 'graduate', label: 'Graduate' },
];

export function PlayerSetup({ players, onAddPlayer, onRemovePlayer }: PlayerSetupProps) {
  const [name, setName] = useState('');
  const [ageGroup, setAgeGroup] = useState<AgeGroup>('kids-6-8');
  const [tier, setTier] = useState<Tier>('student');

  const canAdd = name.trim().length > 0 && players.length < MAX_PLAYERS;

  function handleAdd() {
    if (!canAdd) return;
    const player = createPlayer(name.trim(), ageGroup, tier, players);
    onAddPlayer(player);
    setName('');
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Add Players</h2>
      <p style={styles.subtitle}>
        {players.length}/{MAX_PLAYERS} players added
      </p>

      {/* Player name input */}
      <div style={styles.inputRow}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Player name"
          style={styles.input}
          maxLength={20}
          aria-label="Player name"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
      </div>

      {/* Age group selector */}
      <div style={styles.selectorGroup}>
        <label style={styles.label}>Age Group</label>
        <div style={styles.chips}>
          {AGE_GROUP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              style={{
                ...styles.chip,
                ...(ageGroup === opt.value ? styles.chipActive : {}),
              }}
              onClick={() => setAgeGroup(opt.value)}
              aria-pressed={ageGroup === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tier selector */}
      <div style={styles.selectorGroup}>
        <label style={styles.label}>Difficulty</label>
        <div style={styles.chips}>
          {TIER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              style={{
                ...styles.chip,
                ...(tier === opt.value ? styles.chipActive : {}),
              }}
              onClick={() => setTier(opt.value)}
              aria-pressed={tier === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Add button */}
      <button
        style={{
          ...styles.addButton,
          opacity: canAdd ? 1 : 0.5,
        }}
        onClick={handleAdd}
        disabled={!canAdd}
        aria-label="Add player"
      >
        + Add Player
      </button>

      {/* Player list */}
      {players.length > 0 && (
        <div style={styles.playerList}>
          {players.map((player) => (
            <div key={player.id} style={styles.playerRow}>
              <div style={{ ...styles.dot, backgroundColor: player.color }} />
              <span style={styles.playerName}>{player.name}</span>
              <span style={styles.playerMeta}>
                {AGE_GROUP_OPTIONS.find((a) => a.value === player.ageGroup)?.label} &middot;{' '}
                {TIER_OPTIONS.find((t) => t.value === player.tier)?.label}
              </span>
              <button
                style={styles.removeButton}
                onClick={() => onRemovePlayer(player.id)}
                aria-label={`Remove ${player.name}`}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
    width: '100%',
    maxWidth: '480px',
    margin: '0 auto',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: 'var(--text-light)',
    textAlign: 'center' as const,
  },
  subtitle: {
    fontSize: '0.85rem',
    color: 'var(--muted)',
    textAlign: 'center' as const,
  },
  inputRow: {
    display: 'flex',
    gap: '0.5rem',
  },
  input: {
    flex: 1,
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '2px solid var(--accent-wood)',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text-dark)',
    fontSize: '1rem',
    outline: 'none',
  },
  selectorGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
  },
  chip: {
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-light)',
    fontSize: '0.85rem',
    fontWeight: '500',
    border: '1px solid transparent',
    cursor: 'pointer',
  },
  chipActive: {
    backgroundColor: 'var(--accent-wood)',
    border: '1px solid var(--accent-gold)',
    color: 'var(--text-light)',
  },
  addButton: {
    padding: '0.875rem',
    borderRadius: '10px',
    backgroundColor: 'var(--accent-gold)',
    color: 'var(--text-dark)',
    fontSize: '1rem',
    fontWeight: '700',
    border: 'none',
    cursor: 'pointer',
  },
  playerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  playerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-surface)',
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  playerName: {
    fontWeight: '600',
    color: 'var(--text-light)',
    fontSize: '0.95rem',
  },
  playerMeta: {
    flex: 1,
    fontSize: '0.75rem',
    color: 'var(--muted)',
    textAlign: 'right' as const,
  },
  removeButton: {
    width: '28px',
    height: '28px',
    minHeight: '28px',
    minWidth: '28px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: 'var(--text-light)',
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
};
